import { App } from 'antd';
import { translateApiError } from '@/shared/api/errorToMessage';
import { useArchiveCategoryMutation, useUnarchiveCategoryMutation } from '../api/categoryApi';
import type { CategoryDto } from './types';

/**
 * §2.1: «удалить» группу = отправить в архив (категории не удаляются никогда). Общий для страницы
 * категорий, окна группы и окна категории: подтверждение, вызов API и сообщение об ошибке — в одном
 * месте. Бэкенд отвергает группу с неархивными подкатегориями (`CATEGORY_GROUP_NOT_EMPTY`) — кнопки
 * заранее блокируются по `groupArchiveBlockReason`, а ошибка с бэкенда всё равно показывается.
 */
export function useGroupArchive() {
  const { message, modal } = App.useApp();
  const [archive] = useArchiveCategoryMutation();
  const [unarchive] = useUnarchiveCategoryMutation();

  /** `onDone` вызывается после успешной архивации (например, закрыть окно группы). */
  function archiveGroup(group: Pick<CategoryDto, 'id' | 'name'>, onDone?: () => void) {
    modal.confirm({
      title: `Убрать группу «${group.name}» в архив?`,
      content: 'Группа пропадёт из списков и из каталога клиента. Её можно вернуть из архива.',
      okText: 'В архив',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await archive(group.id).unwrap();
          onDone?.();
        } catch (e) {
          message.error(translateApiError(e));
        }
      },
    });
  }

  async function restoreGroup(group: Pick<CategoryDto, 'id'>) {
    try {
      await unarchive(group.id).unwrap();
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  return { archiveGroup, restoreGroup };
}
